import { Injectable, inject } from '@angular/core'
import { AccountObject, Deal, DealBase, DealRow, DealViewRawDeal, DealViewRawSegments, DealViewShipment, Segment } from '@tradecafe/types/core'
import { DeepPartial, DeepReadonly } from '@tradecafe/types/utils'
import { cloneDeep, omit, sortBy } from 'lodash-es'
import { Observable, combineLatest, of, throwError } from 'rxjs'
import { switchMap, tap } from 'rxjs/operators'
import { DealApiService } from 'src/api/deal'
import { DealFormDto, SegmentFormDto } from 'src/pages/admin/trading/deal-form/deal-form-page/deal-form.schema'
import { DealFormService, prepareDealSegmentPatch } from 'src/pages/admin/trading/deal-form/deal-form-page/deal-form.service'
import { DealFinancialsListenerService } from '../deal-financials-listener'
import { DealElasticSearchService } from './deal-elastic.service'
import { DealFormCalculatorService } from './deal-form-calculator.service'
import { copySegment } from './deal-view.service'

/**
 * Deals Store API requests.
 *
 * - All of these operations should be atomic (one request per operation).
 * - All operations are protected from causing data conflicts.
 * - Some operations are simple patch to the deal object
 * - Some operations are more complex and require deal financial recalculations
 * - Some operations require cascade updates to multiple entities (invoices, shipments, etc).
 * All handled by the Deals Store API.
 */
@Injectable()
export class DealsStoreService {
  private readonly DealApi = inject(DealApiService)
  private readonly DealFinancialsListener = inject(DealFinancialsListenerService)
  private readonly DealForm = inject(DealFormService)
  private readonly DealFormCalculator = inject(DealFormCalculatorService)
  private readonly elastic = inject(DealElasticSearchService)



  storeDeal(deal: DeepReadonly<Pick<Deal, 'deal_id' | 'updated' | 'attributes'>>, dealForm: DeepReadonly<DeepPartial<DealFormDto>>) {
    return this.DealApi.storeDeal({
      updated: deal.updated,
      ...dealForm,
      costs: dealForm.costs?.map(c => omit(c, 'ID'))
    }).pipe(tap(stored => {
      if (dealForm.details.deal_id) {
        this.DealFinancialsListener.tryToRecordChange(deal, stored)
        this.DealApi.dealUpdated$.next(dealForm.details.deal_id)
      }
    }))
  }


  setSegmentDate(
    deal: DeepReadonly<DealRow>,
    segment: DeepReadonly<Segment>,
    segmentPatch: DeepReadonly<Partial<SegmentFormDto>>,
    accounts: DeepReadonly<Dictionary<AccountObject>>,
  ): Observable<Deal> {
    const segmentIndex = deal.segments.findIndex(s => s.segment_id === segment.segment_id)
    if (segmentIndex === -1) {
      return throwError(new Error(`Cannot set segment date: segment ${segment.segment_id} was not found on deal ${deal.deal_id}`))
    }

    // NOTE: this details object is incomplete. deal products and costs are empty as they are not needed here
    const details = this.DealForm.prepareDealTermsAndDatesPatch(deal.raw)
    const segments = deal.segments.map((s, i) => this.DealForm.prepareDealSegmentPatch(s, i))
    const patchedSegments = segments.map((s, i) => i === segmentIndex ? { ...s, ...segmentPatch } : s)
    const segmentForm = patchedSegments[segmentIndex]
    const supplier = accounts[deal.supplier_id]
    const buyer = accounts[deal.buyer_id]
    const detailsPatch = this.DealFormCalculator.calculateTermDateFromSegmentFormImmutable(details, patchedSegments, deal.invoices, segmentForm, supplier, buyer, Object.keys(segmentPatch)[0] as any) || {}
    // TODO: WA-9473: move this.Deals.calculateShippingDates to the backend
    return this.storeDeal(deal, {
      details: {
        deal_id: deal.deal_id,
        ...detailsPatch,
      },
      segments: deal.segments.map(({ segment_id }) =>
        segment_id === segment.segment_id
          ? { ...segmentPatch, segment_id} // patch segment
          : { segment_id }) // keep segment alive
    })
  }

  setSegmentCarrier(
    deal: DeepReadonly<DealRow>,
    segment: DeepReadonly<Segment>,
    carrierId: string,
  ): Observable<Deal> {
    return this.storeDeal(deal, {
      details: { deal_id: deal.deal_id },
      segments: deal.segments.map(({ segment_id }) =>
        segment_id === segment.segment_id
          ? { segment_id, carrierId } // patch segment
          : { segment_id }) // keep segment alive
    })
  }

  setBookingId(deal: DeepReadonly<DealRow>, bookingId: string) {
    const { carrier_id } = deal.earliestVesselOrTruckSegment
    return this.storeDeal(deal, {
      details: { deal_id: deal.deal_id },
      segments: deal.segments.map(s => ({
        segment_id: s.segment_id,
        ...s.carrier_id === carrier_id ? { bookingId } : {},
      })),
    }).pipe(switchMap(() => this.elastic.fetchDeal(deal.deal_id)))
  }

  setBillOfLadingNo(deal: DeepReadonly<DealRow>, billOfLadingNo: string) {
    const vesselSegmentId = deal.earliestVesselSegment?.segment_id;
    const vesselSegmentIndex = deal.segments.findIndex(s => s.segment_id === vesselSegmentId);

    if(!vesselSegmentId || vesselSegmentIndex < 0) return of(deal);

    return this.storeDeal(deal, {
      details: { deal_id: deal.deal_id },
      segments: deal.segments.map((s, index) => ({
        segment_id: s.segment_id,
        ...(index >= vesselSegmentIndex ? { billOfLadingNo } : {})
      }))
    }).pipe(switchMap(() => this.elastic.fetchDeal(deal.deal_id)));
  }

  setVessel(deal: DeepReadonly<DealRow>, vessel: string) {
    const segment = deal.earliestVesselSegment
    if (segment.attributes.vessel === vessel) return of(deal)
    return this.storeDeal(deal, {
      details: { deal_id: deal.deal_id },
      segments: deal.segments.map(s => ({
        segment_id: s.segment_id,
        ...s.segment_id === segment.segment_id ? { vessel } : {},
      }))
    }).pipe(switchMap(() => this.elastic.fetchDeal(deal.deal_id)))
  }

  setSegmentAddress(deal: DeepReadonly<DealRow>, segment: DeepReadonly<Segment>, field: 'exact_loading' | 'exact_dropoff', address: Segment['attributes']['exact_loading'] | Segment['attributes']['exact_dropoff']) {
    return this.storeDeal(deal, {
      details: { deal_id: deal.deal_id },
      segments: deal.segments.map(s => ({
        segment_id: s.segment_id,
        ...s.segment_id === segment.segment_id ? {
          ...field === 'exact_loading' ? {
            exactLoadingAccount: address.account ? parseFloat(address.account) : null,
            exactLoadingAddress: address.address || null,
          } : {
            exactDropoffAccount: address.account ? parseFloat(address.account) : null,
            exactDropoffAddress: address.address || null,
          }
        } : {},
      }))
    }).pipe(switchMap(() => this.elastic.fetchDeal(deal.deal_id)))
  }

  setItemTypeId(deal: DeepReadonly<DealBase>, itemTypeId: string) {
    return this.storeDeal(deal, {
      details: {
        deal_id: deal.deal_id,
      },
      products: deal.offers.map((offer_id, i) => ({
        offer_id,
        bid_id: deal.bids[i],
        ...i === 0 ? { itemTypeId } : {},
      }))
    }).pipe(switchMap(() => this.elastic.fetchDeal(deal.deal_id)))
  }

  setOriginLocation(deal: DeepReadonly<DealBase>, originLocationId: string) {
    return this.storeDeal(deal, {
      details: { deal_id: deal.deal_id, originLocationId },
      products: deal.offers.map((offer_id, i) => ({
        offer_id: offer_id,
        bid_id: deal.bids[i],
        supplierIncotermLocationId: originLocationId,
      }))
    })
  }

  setBuyerIncotermLocation(deal: DeepReadonly<DealBase>, locationId: string) {
    return this.storeDeal(deal, {
      details: { deal_id: deal.deal_id },
      products: deal.offers.map((offer_id, i) => ({
        offer_id: offer_id,
        bid_id: deal.bids[i],
        buyerIncotermLocationId: locationId,
      }))
    })
  }

  setSupplierIncotermLocation(deal: DeepReadonly<DealBase>, locationId: string) {
    return this.storeDeal(deal, {
      details: { deal_id: deal.deal_id },
      products: deal.offers.map((offer_id, i) => ({
        offer_id: offer_id,
        bid_id: deal.bids[i],
        supplierIncotermLocationId: locationId,
      }))
    })
  }

  setDestLocation(deal: DeepReadonly<DealBase>, destLocationId: string) {
    return this.storeDeal(deal, {
      details: { deal_id: deal.deal_id, destLocationId },
      products: deal.offers.map((offer_id, i) => ({
        offer_id: offer_id,
        bid_id: deal.bids[i],
        buyerIncotermLocationId: destLocationId,
      }))
    })
  }

  setContainerId(deal: DeepReadonly<DealRow>, containerNumber: string) {
    const segment = deal.earliestVesselOrTruckSegment as Segment
    if (segment.attributes.container_number === containerNumber) return of(deal)

    return this.storeDeal(deal, {
      details: { deal_id: deal.deal_id },
      segments: deal.segments.map(s => ({
        segment_id: s.segment_id,
        ...s.segment_id === segment.segment_id ? { containerNumber } : {},
      }))
    }).pipe(switchMap(() => this.elastic.fetchDeal(deal.deal_id)))
  }

  setMexInvNo(deal: DeepReadonly<DealRow>, mexInvNo: string) {
    const foreignInvoices = cloneDeep(deal.foreign_invoices) as DealBase['foreign_invoices'] || []
    let mxInvoice = foreignInvoices.find(i => i.country_code === 'MX')
    if (!mxInvoice) foreignInvoices.push({ country_code: 'MX', number: mexInvNo })
    else mxInvoice.number = mexInvNo

    return this.storeDeal(deal, {
      details: { deal_id: deal.deal_id, foreignInvoices }
    }).pipe(switchMap(() => this.elastic.fetchDeal(deal.deal_id)))
  }

  copySegments(
    source: DeepReadonly<DealViewRawDeal & DealViewRawSegments>,
    destination: DeepReadonly<Array<DealViewRawDeal & DealViewRawSegments & DealViewShipment>>,
    deleteSegments: boolean,
    exactCopy: boolean,
  ): Observable<Deal[]> {
    const actions = destination.map(dv => {
      const dealForm: Partial<DealFormDto> = {
        details: { deal_id: dv.deal.deal_id },
        segments: []
      }
      // keep old segments
      if (!deleteSegments && dv.segments?.length) {
        sortBy(dv.segments, 'order').forEach(s => dealForm.segments.push({ segment_id: s.segment_id }))
      }

      // add new segments
      sortBy(source.segments, 'order').forEach(s => {
        const newSegment = copySegment(s, {
          mode: exactCopy ? 'exact' : 'default',
        })
        const { segment: { segment_id }, ...segmentForm } = prepareDealSegmentPatch(newSegment, dealForm.segments.length)
        dealForm.segments.push({ ...segmentForm, segment_id })
      })

      // update segments order
      dealForm.segments.forEach((segment, index) => {
        segment.order = index
      })
      // copy shipment infor stored in the "source" deal
      if (exactCopy) {
        dealForm.details.shipmentErd = source.deal.attributes.shipment?.erd_date
        dealForm.details.shipmentEpd = source.deal.attributes.shipment?.epd_date
        dealForm.details.shipmentVoyageNo = source.deal.attributes.shipment?.voyage_no
        // TODO: WA-7300: this.Deals.calculateShippingDates(deal)
      }

      return { deal: dv.deal, dealForm }
    })

    return combineLatest(actions.map(a => this.storeDeal(a.deal, a.dealForm)))
  }
}
